import React, { useState, useMemo } from 'react';
import { Expense } from '../types';
import { RowActions, RowActionCell } from './RowActions';
import {
  PageHeader, EmptyState, FormScreen, useFormScreenScroll,
  Button, TextButton, Chip, Tag, List, Field, Input, Select, TYPE, cn,
} from './ui';

type ExpenseDraft = {
  name: string; amount: number; dueDay: number; category: string;
  frequency: 'monthly' | 'weekly' | 'one-time';
  dueDate: string; startDate: string; endDate: string; isActive: boolean;
};

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** One field set for both add and edit. These were two near-identical 150-line
 *  blocks that had already drifted apart. */
const ExpenseFields: React.FC<{
  value: ExpenseDraft;
  onChange: (next: ExpenseDraft) => void;
  categories: string[];
  idPrefix: string;
}> = ({ value, onChange, categories, idPrefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
    <Field label="Expense name" htmlFor={`${idPrefix}-name`}>
      <Input
        id={`${idPrefix}-name`}
        type="text"
        required
        value={value.name}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
      />
    </Field>

    <Field label="Amount" htmlFor={`${idPrefix}-amount`}>
      <Input
        id={`${idPrefix}-amount`}
        type="number"
        required
        step="0.01"
        value={value.amount}
        onChange={(e) => onChange({ ...value, amount: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 })}
      />
    </Field>

    <Field label="Frequency" htmlFor={`${idPrefix}-frequency`}>
      <Select
        id={`${idPrefix}-frequency`}
        value={value.frequency}
        onChange={(e) => onChange({
          ...value,
          frequency: e.target.value as ExpenseDraft['frequency'],
          dueDay: e.target.value === 'weekly' ? 0 : 1,
        })}
      >
        <option value="monthly">Monthly</option>
        <option value="weekly">Weekly</option>
        <option value="one-time">One-time</option>
      </Select>
    </Field>

    {value.frequency === 'one-time' ? (
      <Field label="Due date" htmlFor={`${idPrefix}-duedate`}>
        <Input
          id={`${idPrefix}-duedate`}
          type="date"
          required
          value={value.dueDate}
          onChange={(e) => onChange({ ...value, dueDate: e.target.value })}
        />
      </Field>
    ) : (
      <>
        <Field
          label={value.frequency === 'monthly' ? 'Due day' : 'Day of week'}
          hint={value.frequency === 'monthly' ? 'Clamped to the month length.' : undefined}
          htmlFor={`${idPrefix}-dueday`}
        >
          {value.frequency === 'monthly' ? (
            <Input
              id={`${idPrefix}-dueday`}
              type="number"
              required
              min="1"
              max="31"
              value={value.dueDay}
              onChange={(e) => onChange({ ...value, dueDay: e.target.value === '' ? 1 : parseInt(e.target.value) || 1 })}
            />
          ) : (
            <Select
              id={`${idPrefix}-dueday`}
              value={value.dueDay}
              onChange={(e) => onChange({ ...value, dueDay: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 })}
            >
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </Select>
          )}
        </Field>

        <Field label="Start date" htmlFor={`${idPrefix}-start`}>
          <Input
            id={`${idPrefix}-start`}
            type="date"
            required
            value={value.startDate}
            onChange={(e) => onChange({ ...value, startDate: e.target.value })}
          />
        </Field>

        <Field label="End date" hint="Leave blank for no end date." htmlFor={`${idPrefix}-end`}>
          <Input
            id={`${idPrefix}-end`}
            type="date"
            value={value.endDate}
            onChange={(e) => onChange({ ...value, endDate: e.target.value })}
          />
        </Field>
      </>
    )}

    <Field label="Category" htmlFor={`${idPrefix}-category`}>
      <Select
        id={`${idPrefix}-category`}
        value={value.category}
        onChange={(e) => onChange({ ...value, category: e.target.value })}
      >
        <option value="">Select category</option>
        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
      </Select>
    </Field>
  </div>
);


interface ExpensesManagerProps {
  expenses: Expense[];
  onAddExpense: (expense: Omit<Expense, 'id'>) => void;
  onUpdateExpense: (id: string, expense: Partial<Expense>) => void;
  onDeleteExpense: (id: string) => void;
}

// Helper function to parse date string in local timezone
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

// Helper function to format a UTC date to local date string (without timezone shift)
/** Name / day / frequency / category / since / amount / status. Below md the
 *  middle five drop away and the row is name, amount, status. */
const EXP_COLS =
  'grid grid-cols-[1.7fr_1fr_.9fr] md:grid-cols-expenses gap-x-4 items-center';

const ExpCol: React.FC<{ label: string; align?: string; deskOnly?: boolean }> = ({
  label, align = 'text-left', deskOnly,
}) => (
  <div className={cn(TYPE.sectionLabel, 'text-ink-muted', align, deskOnly && 'hidden md:block')}>
    {label}
  </div>
);

const formatUTCDate = (date: Date): string => {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()).toLocaleDateString();
};

export const ExpensesManager: React.FC<ExpensesManagerProps> = ({
  expenses,
  onAddExpense,
  onUpdateExpense,
  onDeleteExpense
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);

  const rememberListScroll = useFormScreenScroll(showAddForm || editingExpenseId !== null);
  const [sortBy, setSortBy] = useState<'name' | 'amount' | 'dueDay' | 'category' | 'frequency'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterFrequency, setFilterFrequency] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Category, frequency and status are one-off choices; keeping all three
  // expanded cost half the phone viewport before a single expense appeared.
  const [showFilters, setShowFilters] = useState(false);
  const [newExpense, setNewExpense] = useState({
    name: '',
    amount: 0,
    dueDay: 1,
    category: '',
    frequency: 'monthly' as 'monthly' | 'weekly' | 'one-time',
    dueDate: new Date().toISOString().split('T')[0],
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    isActive: true
  });
  const [editExpense, setEditExpense] = useState({
    name: '',
    amount: 0,
    dueDay: 1,
    category: '',
    frequency: 'monthly' as 'monthly' | 'weekly' | 'one-time',
    dueDate: new Date().toISOString().split('T')[0],
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    isActive: true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const expenseToAdd = {
      ...newExpense,
      dueDate: newExpense.frequency === 'one-time' && newExpense.dueDate
        ? parseLocalDate(newExpense.dueDate)
        : undefined,
      startDate: (newExpense.frequency === 'monthly' || newExpense.frequency === 'weekly') && newExpense.startDate
        ? parseLocalDate(newExpense.startDate)
        : undefined,
      endDate: newExpense.endDate
        ? parseLocalDate(newExpense.endDate)
        : undefined
    };
    onAddExpense(expenseToAdd);
    setNewExpense({
      name: '',
      amount: 0,
      dueDay: 1,
      category: '',
      frequency: 'monthly',
      dueDate: new Date().toISOString().split('T')[0],
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      isActive: true
    });
    setShowAddForm(false);
  };

  const startEdit = (expense: Expense) => {
    rememberListScroll();
    setEditingExpenseId(expense.id);
    setEditExpense({
      name: expense.name,
      amount: expense.amount,
      dueDay: expense.dueDay || 1,
      category: expense.category,
      frequency: expense.frequency,
      dueDate: expense.dueDate ? new Date(expense.dueDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      startDate: expense.startDate ? new Date(expense.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      endDate: expense.endDate ? new Date(expense.endDate).toISOString().split('T')[0] : '',
      isActive: expense.isActive
    });
  };

  const handleEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingExpenseId) {
      const expenseToUpdate = {
        ...editExpense,
        dueDate: editExpense.frequency === 'one-time' && editExpense.dueDate
          ? parseLocalDate(editExpense.dueDate)
          : undefined,
        startDate: (editExpense.frequency === 'monthly' || editExpense.frequency === 'weekly') && editExpense.startDate
          ? parseLocalDate(editExpense.startDate)
          : undefined,
        endDate: editExpense.endDate
          ? parseLocalDate(editExpense.endDate)
          : undefined
      };
      onUpdateExpense(editingExpenseId, expenseToUpdate);
      setEditingExpenseId(null);
    }
  };

  const cancelEdit = () => {
    setEditingExpenseId(null);
  };

  const handleClone = (expense: Expense) => {
    const clonedExpense = {
      name: `${expense.name} (Copy)`,
      amount: expense.amount,
      dueDay: expense.dueDay || 1,
      category: expense.category,
      frequency: expense.frequency,
      dueDate: expense.dueDate ? new Date(expense.dueDate) : undefined,
      startDate: expense.startDate ? new Date() : undefined, // Use today's date for the clone
      endDate: expense.endDate ? new Date(expense.endDate) : undefined,
      isActive: true // Clone as active by default
    };
    onAddExpense(clonedExpense);
  };

  const categories = ['Food', 'Health', 'Pet', 'Housing', 'Utilities', 'Insurance', 'Subscriptions', 'Other'];

  // Filter and sort expenses
  const filteredAndSortedExpenses = useMemo(() => {
    let filtered = expenses;

    // Apply filters
    if (filterCategory) {
      filtered = filtered.filter(expense => expense.category === filterCategory);
    }

    if (filterFrequency) {
      filtered = filtered.filter(expense => expense.frequency === filterFrequency);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(expense =>
        filterStatus === 'active' ? expense.isActive : !expense.isActive
      );
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(expense =>
        expense.name.toLowerCase().includes(searchLower) ||
        expense.category.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy];
      let bValue: any = b[sortBy];

      // Handle special cases
      if (sortBy === 'dueDay') {
        aValue = a.dueDay || 0;
        bValue = b.dueDay || 0;
      }

      // Convert to strings for consistent comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [expenses, sortBy, sortOrder, filterCategory, filterFrequency, filterStatus, searchTerm]);

  // Labels are display-only. The values are what the filter and sort logic
  // compare against and what the API stores, so they stay lowercase.
  const FREQUENCY_OPTIONS = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'one-time', label: 'One-time' },
  ];
  const STATUS_OPTIONS: { value: typeof filterStatus; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];
  const SORT_OPTIONS: { value: typeof sortBy; label: string }[] = [
    { value: 'name', label: 'Name' },
    { value: 'amount', label: 'Amount' },
    { value: 'dueDay', label: 'Due day' },
    { value: 'category', label: 'Category' },
    { value: 'frequency', label: 'Frequency' },
  ];


  const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
  /** What the filtered set costs in a typical month. One-time commitments are
   *  excluded rather than smeared across twelve months — a $900 HOA bill due
   *  once is not $75 a month of commitment. */
  const monthlyEquivalent = filteredAndSortedExpenses
    .filter(e => e.isActive)
    .reduce((sum, e) => {
      if (e.frequency === 'monthly') return sum + e.amount;
      if (e.frequency === 'weekly') return sum + (e.amount * 52) / 12;
      return sum;
    }, 0);

  const hasFilters = Boolean(filterCategory || filterFrequency || filterStatus !== 'all' || searchTerm);
  /** What the disclosure hides — search and sort stay on screen, so they
   *  are not counted here. */
  const activeFilterCount =
    (filterCategory ? 1 : 0) + (filterFrequency ? 1 : 0) + (filterStatus !== 'all' ? 1 : 0);
  const describe = (expense: Expense) =>
    expense.frequency === 'monthly'
      ? `Day ${expense.dueDay}`
      : expense.frequency === 'weekly'
        ? `${DAYS[expense.dueDay || 0]}s`
        : expense.dueDate ? formatUTCDate(new Date(expense.dueDate)) : 'TBD';

  // Placed after every hook, so the hook order never changes between renders.
  const editingExpense = editingExpenseId
    ? expenses.find(e => e.id === editingExpenseId) ?? null
    : null;

  if (showAddForm) {
    return (
      <FormScreen
        backLabel="Back to expenses"
        heading="New expense"
        sectionTitle="Details"
        hint="What it is, and how often it repeats. Dates and category can be left for later."
        submitLabel="Add expense"
        onSubmit={handleSubmit}
        onCancel={() => setShowAddForm(false)}
      >
        <ExpenseFields
          value={newExpense}
          onChange={setNewExpense}
          categories={categories}
          idPrefix="new-expense"
        />
      </FormScreen>
    );
  }

  if (editingExpense) {
    return (
      <FormScreen
        backLabel="Back to expenses"
        heading="Edit expense"
        subject={editingExpense.name}
        sectionTitle="Details"
        hint="What it is, and how often it repeats. Dates and category can be left for later."
        submitLabel="Save changes"
        onSubmit={handleEdit}
        onCancel={cancelEdit}
      >
        <ExpenseFields
          value={editExpense}
          onChange={setEditExpense}
          categories={categories}
          idPrefix="edit-expense"
        />
      </FormScreen>
    );
  }

  return (
    <>
      <PageHeader
        heading="Monthly Expenses"
        subhead="Everything that leaves the account on a schedule."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              rememberListScroll();
              setShowAddForm(true);
            }}
          >
            Add expense
          </Button>
        }
      />

      <section className="mt-11">
        {expenses.length === 0 ? (
          <EmptyState>No expenses added yet</EmptyState>
        ) : (
          <List>
            {/* The controls belong to the list, so they live in its card as a
                header rather than in a slab of their own above it. Search is
                capped — a field the width of the page for a fifteen-character
                query was the loudest thing on the screen. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-3 px-4 py-3 border-b border-rule">
              <Input
                id="expense-search"
                type="text"
                aria-label="Search expenses"
                placeholder="Search name or category"
                className="w-full sm:w-[260px]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <div className="flex items-center gap-2 sm:ml-auto">
                <label htmlFor="expense-sort" className={cn(TYPE.metaTight, 'text-ink-muted')}>
                  Sort
                </label>
                <Select
                  id="expense-sort"
                  className="w-[124px]"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                >
                  {SORT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
                <Chip
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  aria-label={sortOrder === 'asc' ? 'Sorted ascending' : 'Sorted descending'}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </Chip>
                <Chip
                  active={showFilters || activeFilterCount > 0}
                  onClick={() => setShowFilters(open => !open)}
                  aria-expanded={showFilters}
                  aria-controls="expense-filter-panel"
                >
                  Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </Chip>
              </div>
            </div>

            {showFilters && (
              <div
                id="expense-filter-panel"
                className="border-b border-rule px-4 py-4 flex flex-col gap-3 bg-ground-sunk"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn(TYPE.metaTight, 'text-ink-muted w-[72px] shrink-0')}>Category</span>
                  <Chip active={filterCategory === ''} onClick={() => setFilterCategory('')}>All</Chip>
                  {categories.map(cat => (
                    <Chip key={cat} active={filterCategory === cat} onClick={() => setFilterCategory(cat)}>
                      {cat}
                    </Chip>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn(TYPE.metaTight, 'text-ink-muted w-[72px] shrink-0')}>Frequency</span>
                  <Chip active={filterFrequency === ''} onClick={() => setFilterFrequency('')}>All</Chip>
                  {FREQUENCY_OPTIONS.map(f => (
                    <Chip key={f.value} active={filterFrequency === f.value} onClick={() => setFilterFrequency(f.value)}>
                      {f.label}
                    </Chip>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn(TYPE.metaTight, 'text-ink-muted w-[72px] shrink-0')}>Status</span>
                  {STATUS_OPTIONS.map(st => (
                    <Chip key={st.value} active={filterStatus === st.value} onClick={() => setFilterStatus(st.value)}>
                      {st.label}
                    </Chip>
                  ))}
                </div>
              </div>
            )}

            {filteredAndSortedExpenses.length === 0 && (
              <div className="px-4 py-10 text-center">
                <p className={cn(TYPE.small, 'text-ink-muted')}>No expenses match the current filters</p>
                {hasFilters && (
                  <div className="mt-3">
                    <TextButton
                      onClick={() => {
                        setFilterCategory('');
                        setFilterFrequency('');
                        setFilterStatus('all');
                        setSearchTerm('');
                      }}
                    >
                      Clear filters
                    </TextButton>
                  </div>
                )}
              </div>
            )}

            {/* Column heads sit under the toolbar, on the sunk ground, so the
                table reads as one object with its controls. */}
            <div className={cn(EXP_COLS, 'px-4 py-2.5 bg-ground-sunk border-b border-rule-base')}>
              <ExpCol label="Name" />
              <ExpCol label="Day" align="text-center" deskOnly />
              <ExpCol label="Frequency" deskOnly />
              <ExpCol label="Category" deskOnly />
              <ExpCol label="Since" deskOnly />
              <ExpCol label="Amount" align="text-right" />
              <ExpCol label="Status" align="text-right" />
            </div>

            {filteredAndSortedExpenses.map(expense => (
              <div
                key={expense.id}
                onClick={() => startEdit(expense)}
                className={cn(
                  EXP_COLS,
                  'px-4 py-3 border-b border-rule last:border-b-0',
                  'cursor-pointer hover:bg-ground-sunk transition-colors duration-[120ms]',
                  !expense.isActive && 'opacity-60'
                )}
              >
                <div className="min-w-0">
                  {/* The row is not itself a button — it contains one. The name
                      is the keyboard-reachable affordance. */}
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      startEdit(expense);
                    }}
                    className={cn(
                      TYPE.rowTitle,
                      'text-ink truncate block max-w-full text-left',
                      'hover:text-accent transition-colors duration-[120ms]'
                    )}
                  >
                    {expense.name}
                  </button>
                </div>

                <div className={cn(TYPE.meta, 'hidden md:block text-ink-soft text-center')}>
                  {describe(expense)}
                </div>
                <div className="hidden md:block text-[13px] text-ink-soft">{expense.frequency}</div>
                <div className="hidden md:block text-[13px] text-ink-soft truncate">
                  {expense.category || '—'}
                </div>
                <div className={cn(TYPE.meta, 'hidden md:block text-ink-muted')}>
                  {expense.startDate && expense.frequency !== 'one-time'
                    ? formatUTCDate(new Date(expense.startDate))
                    : '—'}
                </div>

                <div className={cn(TYPE.rowFigure, 'text-money-out text-right')}>
                  {usd(expense.amount)}
                </div>

                <RowActionCell className="flex items-center justify-end gap-2">
                  {!expense.isActive && <Tag weight="bone">Paused</Tag>}
                  <RowActions
                    label={expense.name}
                    onEdit={() => startEdit(expense)}
                    onClone={() => handleClone(expense)}
                    onTogglePause={() => onUpdateExpense(expense.id, { isActive: !expense.isActive })}
                    isPaused={!expense.isActive}
                    onDelete={() => onDeleteExpense(expense.id)}
                  />
                </RowActionCell>
              </div>
            ))}

            {filteredAndSortedExpenses.length > 0 && (
              <div className={cn(EXP_COLS, 'px-4 py-3.5 rule-total')}>
                <div className={cn(TYPE.sectionLabel, 'text-ink-muted')}>
                  {filteredAndSortedExpenses.length} rows
                </div>
                <div className="hidden md:block" />
                <div className="hidden md:block" />
                <div className="hidden md:block" />
                <div className={cn(TYPE.meta, 'hidden md:block text-ink-muted')}>
                  monthly equivalent
                </div>
                <div className={cn(TYPE.rowFigure, 'text-ink text-right')}>
                  {usd(monthlyEquivalent)}
                </div>
                <div className={cn(TYPE.meta, 'text-ink-muted text-right')}>
                  {filteredAndSortedExpenses.filter(e => !e.isActive).length} paused
                </div>
              </div>
            )}
          </List>
        )}
      </section>
    </>
  );
};